import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import AddShiftContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftContextualButton'
import DropItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CellActions/EmptyCellActions'
import { useDropShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'

import DisabledNightShiftCell from '../Cells/DisabledNightShiftCell'
import EmptyCell from '../Cells/EmptyCell'
import ShiftCell from '../Cells/ShiftCell'
import BankHolidayItem from '../Cells/ShiftCell/BankHolidayItem'

interface Props {
  date: moment.Moment
  shiftsRow: ShiftsRow
  isFirstDay: boolean
  visibleDefaultShifts: DefaultShifts
}

const WorkAreaDayCell = ({ date, shiftsRow, isFirstDay, visibleDefaultShifts }: Props) => {
  const [{ view }] = useSchedule()
  const orderedShifts = shiftsRow.orderedShiftsByDate(date, view)
  const orderedNightShifts = isFirstDay ? shiftsRow.orderedNightShiftsPeriodBefore(date) : []
  const nightShiftsToDisplay = orderedNightShifts.filter(
    (shift) => !shift.isEndNightShiftExactMidnight(date)
  )

  const locationId = shiftsRow.keyReference
  const holidays = shiftsRow.holidays

  const filteredHolidays = holidays?.filterByDateAndLocation(date, locationId)

  // There can only be 1 holiday and 1 rest day per date due to a
  // date uniqueness constraint in the backend models
  const holiday = filteredHolidays?.length ? filteredHolidays[0] : null

  const canManageShifts = shiftsRow.canManage

  const onlyNightShifts = !!nightShiftsToDisplay?.length && orderedShifts.length == 0

  const {
    isOver: isOverParent,
    canDrop,
    drop,
  } = useDropShift({
    shiftsRow,
    date,
  })

  return (
    <>
      <Box gap="s4" ref={drop}>
        {!!holiday && <BankHolidayItem holiday={holiday} />}

        {!!orderedNightShifts.length && (
          <DisabledNightShiftCell date={date} nightShiftsPeriodBefore={nightShiftsToDisplay} />
        )}

        {orderedShifts.map((shift) => (
          <ShiftCell
            key={`shifts-${shift.id}-${shiftsRow.key}`}
            shifts={[shift]}
            shiftsRow={shiftsRow}
            date={date}
          />
        ))}

        {!!orderedShifts.length && canDrop && isOverParent && (
          <DropItem
            shiftsRow={shiftsRow}
            date={date}
            isOver={canDrop && isOverParent}
            visibleDefaultShifts={visibleDefaultShifts}
          />
        )}

        {canManageShifts && !onlyNightShifts && (
          <AddShiftContextualButton
            date={date}
            shiftsRow={shiftsRow}
            visibleDefaultShifts={visibleDefaultShifts}
          />
        )}

        {canManageShifts && !orderedShifts.length && (
          <Box flexDirection="column" height="full" flexGrow ref={drop}>
            <EmptyCell
              date={date}
              shiftsRow={shiftsRow}
              isOver={canDrop && isOverParent}
              visibleDefaultShifts={visibleDefaultShifts}
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default observer(WorkAreaDayCell)
